// DEPENDENCIES ---------------------------------------------------------------- //

import React, { useMemo, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { BiListUl, BiGridAlt, BiMap } from 'react-icons/bi';

import DataCard, { type ActiveFilter, type ViewModeOption } from '@components/DataCard';
import { PAGE_SX } from '@hopdrive/sdk-theme';
import { useOpenProductAction, useShellTabActions } from '@features/products/shell/shellTabActions';
import { REQUEST_RIDE_ACTION_KEY, capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import { useGuestRidesCounts } from '../counts/useGuestRidesCounts';
import { useGuestRideSubscription } from '../hooks/useGuestRideSubscription';
import { useGuestRideStore, type GuestRideViewMode } from '../stores/guestRideStore';
import { useRidesTabData } from './useRidesTabData';

import RidesTabStats from './RidesTabStats';
import GuestRidesTable from '../components/GuestRidesTable';
import GuestRidesGrid from '../components/GuestRidesGrid';
import GuestRidesMapView from '../components/GuestRidesMapView';
import GuestRideDateFilter from '../components/GuestRideDateFilter';

// TYPES ---------------------------------------------------------------- //


// CONSTANTS ---------------------------------------------------------------- //

const VIEW_MODES: ViewModeOption[] = [
  { value: 'list', icon: <BiListUl />, label: 'Table View' },
  { value: 'grid', icon: <BiGridAlt />, label: 'Grid View' },
  { value: 'map', icon: <BiMap />, label: 'Map View' },
];

// COMPONENT ---------------------------------------------------------------- //

/**
 * The Rides tab of the Guest Rides shell. The layout owns the frame, the header actions (Request
 * Ride and Share Code are the product's actions), and the state banner; this tab registers its
 * refresh, fills the banner slot with the credit panel, and keeps the date filter as a control.
 */
const RidesTab: React.FC = () => {
  const {
    rides,
    filteredCount,
    customerId,
    window,
    statusFilter,
    search,
    page,
    rowsPerPage,
    viewMode,
    sortBy,
    sortOrder,
    highlightedRideId,
    loading,
    error,
    refetch,
    setStatusFilter,
    setSearch,
    setPage,
    setRowsPerPage,
    setViewMode,
    setSorting,
    clearFilters,
    setHighlightedRideId,
  } = useRidesTabData();

  const { counts, loading: countsLoading, refetch: refetchCounts } = useGuestRidesCounts({ customerIds: [customerId], window });

  // A pushed change refreshes the list and the figures together.
  const refetchAll = useCallback(() => {
    void refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);
  useGuestRideSubscription(refetchAll);

  // Create gate: the envelope's createNew (entitlement, credit standing) composed with the
  // viewer gate — one decision source instead of a per-page contract lookup.
  const entry = useProductEntry('guest-rides');
  const canRequestRide = capabilityGate(entry, 'createNew').kind === 'render';

  const { filters: storeFilters, setFilters } = useGuestRideStore();

  // Request Ride and Share Code are the product's actions, mounted by the layout; this tab
  // contributes its refresh and opens the product's Request Ride from its empty state.
  const openProductAction = useOpenProductAction();
  const tabActions = useMemo(() => ({ sync: refetchAll }), [refetchAll]);
  useShellTabActions(tabActions);
  const openRequestRide = useCallback(() => openProductAction(REQUEST_RIDE_ACTION_KEY), [openProductAction]);

  const hasData = (counts?.buckets.total ?? 0) > 0;

  // ── Active filters (from KPI cards + search + date range) ──────────────
  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const filters: ActiveFilter[] = [];
    if (statusFilter !== 'all') {
      const label = statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);
      filters.push({ key: 'status', label: `Status: ${label}` });
    }
    if (search.trim()) {
      filters.push({ key: 'search', label: `"${search}"` });
    }
    const rangeLabels: Record<string, string> = {
      today: 'Today',
      yesterday: 'Yesterday',
      thisweek: 'This Week',
      lastweek: 'Last Week',
      thismonth: 'This Month',
      lastmonth: 'Last Month',
      custom: 'Custom Range',
    };
    filters.push({ key: 'dateRange', label: `Date: ${rangeLabels[storeFilters.dateRange] || storeFilters.dateRange}` });
    return filters;
  }, [statusFilter, search, storeFilters.dateRange]);

  // ── Per-filter clear ────────────────────────────────────────────────────
  const clearFilter = useCallback(
    (key: string) => {
      if (key === 'status') setStatusFilter('all');
      if (key === 'search') setSearch('');
      if (key === 'dateRange') setFilters({ dateRange: 'today', startDate: '', endDate: '' });
    },
    [setStatusFilter, setSearch, setFilters],
  );

  const handleHighlightClear = useCallback(() => {
    setHighlightedRideId(null);
  }, [setHighlightedRideId]);

  const content = (
    <>
      {/* Error */}
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Error loading guest rides: {error.message}
        </Alert>
      )}

      {/* KPI Cards */}
      <RidesTabStats
        counts={counts}
        selectedFilter={statusFilter}
        onFilterChange={setStatusFilter}
        loading={countsLoading && !hasData}
      />

      {/* Data Card — toolbar + content + pagination */}
      <DataCard
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder='Search guest rides...'
        viewMode={viewMode}
        onViewModeChange={mode => setViewMode(mode as GuestRideViewMode)}
        viewModes={VIEW_MODES}
        activeFilters={activeFilters}
        onClearFilter={clearFilter}
        onClearAllFilters={clearFilters}
        loading={loading}
        totalCount={filteredCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={e => setRowsPerPage(parseInt(e.target.value, 10))}
        showPagination={viewMode !== 'map'}
        resource='Guest Rides'
        hasData={hasData}
        // Hide the empty-state CTA entirely when there's no active contract —
        // the disabled header button + tooltip carry the message; offering a
        // CTA we'd then block on click would just be misleading.
        emptyCtaLabel={canRequestRide ? 'Request Ride' : undefined}
        onEmptyCtaClick={canRequestRide ? openRequestRide : undefined}
      >
        {/* Table view */}
        {viewMode === 'list' && (
          <GuestRidesTable
            rides={rides}
            loading={loading}
            hasData={hasData}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={setSorting}
            onClearFilters={clearFilters}
            highlightedRideId={highlightedRideId}
            onHighlightClear={handleHighlightClear}
          />
        )}

        {/* Grid view */}
        {viewMode === 'grid' && (
          <GuestRidesGrid
            rides={rides}
            loading={loading}
            highlightedRideId={highlightedRideId}
            onHighlightClear={handleHighlightClear}
          />
        )}

        {/* Map view */}
        {viewMode === 'map' && <GuestRidesMapView rides={rides} loading={loading} />}
      </DataCard>
    </>
  );

  return (
    <Box sx={PAGE_SX}>
      {/* The tab's controls row: the date range, a control, sits with the content it shapes. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <GuestRideDateFilter />
      </Box>
      {content}
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default RidesTab;
